<template>
    <div class="info-attachment">
        <hh-icon-btn :class="`info-attachment__button${this.showInfo ? ' info-attachment__button--active' : ''}`"
                     ref="button"
                     root-class="info-attachment__button"
                     icon="info"
                     @click="onInfoClick"
                     @close="onInfoClose"
                     @keyup.esc="onInfoClose" />
        <div v-if="showInfo"
             ref="info"
             class="info-attachment__info">
            <div class="info-attachment__info__inner">
                <i :class="`info-attachment__info__close fas ${this.scheme.resources.brandedImagery.closeIcon}`"
                   @click="onInfoClose" />
                <h1 v-if="title"
                    class="info-attachment__info__header">
                    {{ title }}
                </h1>
                <div class="info-attachment__info__content">
                    <img v-if="imageUrl"
                         ref="image"
                         class="info-attachment__info__content__image"
                         :src="imageUrl"
                         :alt="altText">
                    <p class="info-attachment__info__content__text">
                        {{ text }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import Viewport from '../../helpers/viewport'

export default {

    props: {
        title: {
            type: String,
            default: null,
        },
        text: {
            type: String,
            required: true,
        },
        imageUrl: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            showInfo: false,
        }
    },

    computed: {

        /**
         * @returns {string}
         */
        altText() {
            return this.title != null ? this.title : 'Information'
        },

        /**
         * @returns {Scheme}
         */
        scheme() {
            return this.$scheme
        },
    },

    mounted() {
        this.$eventbus.$on(this.$appevents.infoAttachmentShown, (show, onMaskClick) => {
            if (show) {
                this.$eventbus.$emit(this.$appevents.showMask, 'under-info-attachment', {
                    onMaskClick,
                    cssModifier: '--info-attachment-mask',
                })
            } else {
                this.showInfo = false
                this.$eventbus.$emit(this.$appevents.hideMask)
            }
        })
    },

    unmounted() {
        this.$eventbus.$off(this.$appevents.infoAttachmentShown)
    },

    methods: {

        _adjustPosition() {
            const viewport = new Viewport()

            viewport.bpMinMid(() => {
                let addImgHeight = 0

                if (this.imageUrl != null) {
                    const imageBounding = this.$refs.image ? this.$refs.image.getBoundingClientRect() : null
                    if (!imageBounding || imageBounding.height === 0) addImgHeight = 200
                }

                const info = this.$refs.info
                const infoBounding = info ? info.getBoundingClientRect() : null
                const infoHeight = infoBounding ? infoBounding.height + addImgHeight + 30 : 0
                const relativeFromTop = infoHeight ? infoHeight + infoBounding.y : 0
                const relativeFromBottom = infoHeight ? infoBounding.y - infoHeight : 0

                if (relativeFromTop > viewport.height) {
                    if (relativeFromBottom > 50)
                        info.setAttribute('style', 'top:auto; bottom: 0;')
                    else
                        info.setAttribute('style', `top:auto; bottom: -${ infoHeight/2 }px;`)

                }
            })
        },

        onInfoClose() {
            this.$eventbus.$emit(this.$appevents.infoAttachmentShown, false)
        },

        async onInfoClick() {
            this.showInfo = !this.showInfo
            await this.$nextTick()
            this._adjustPosition()
            this.$eventbus.$emit(this.$appevents.infoAttachmentShown, this.showInfo, () => {
                this.showInfo = false
            })
        },
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\global\_hh-info-attachment.scss

$header-height: 46px;

.info-attachment {
    @include user-select-none;

    @include element('button') {
        font-size: 1.2rem;
        cursor: pointer;
        position: relative;
        top: -3px;

        @include bp-min-small {
            top: 0;
        }

        @include modifier('active') {

            @include bp-min-mid {
                @include z-index('info-attachment-button');
            }
        }
    }

    @include element('info') {
        cursor: pointer;
        position: fixed;
        width: 90%;
        right: 50%;
        top: rem(100px);
        bottom: rem(160px);
        max-height: rem(510px);
        margin-right: -45%;
        @include z-index('info-attachment');

        *:last-child {
            margin-bottom: 0;
        }

        @include bp-min-small {
            width: rem(420px);
            top: rem(160px);
            bottom: auto;
            margin-right: rem(-210px);
        }

        @include bp-min-mid {
            position: absolute;
            right: 1.2rem;
            top: 0;
            margin-right: 0;
        }

        @include element('inner') {
            height: 100%;
        }

        @include element('close') {
            display: block;
            position: absolute;
            right: 4px;
            top: 2px;
            font-size: 1.6rem;
            z-index: z-index('raised');

            &:hover, &:active {
                cursor: pointer;
                color: $error;
            }

            @include bp-min-mid {
                display: none;
            }
        }

        @include element('content') {
            height: calc(100% - #{$header-height});
            overflow: auto;

            @include element('image') {
                display: block;
                max-width: 100%;
                max-height: 200px;
                margin: 0 auto 1rem auto;
            }

            @include element('text') {
                font-size: 0.9rem;
                text-align: justify;
            }
        }
    }
}

</style>
